/**
 * monthlyBill — the BROWSER MIRROR of what a client actually pays us.
 *
 * The server owner is `backend/functions/src/billing/monthlyBill.ts`. This is a
 * mirror for the same reason `documentTotals.ts` and `leadAttribution.ts` are:
 * the backend cannot import from `app/shared`, and the Dashboard already holds
 * the client document, so a round trip to restate a number it is looking at
 * would be latency for nothing.
 *
 * 🔴 WHAT IT REPLACES (2026-09-13, Control Center parity Phase 1):
 *
 *     Dashboard.tsx:153
 *       client?.monthlyBill ?? (BASE_PRICE + hiredAgents.length * AGENT_PRICE)
 *
 * `monthlyBill` is MISSING ON EVERY CLIENT — measured, not assumed — so that was
 * never a fallback, it was the only path, and it recomputes the LIST price while
 * every subscription carries a discount. Every client was shown more than they
 * pay, and Todd, who is comped, was shown "$600/mo investment".
 *
 * ⚠️ A MIRROR WITH NO GATE IS THE DUPLICATION THIS AUDIT EXISTS TO FIND. Both
 * sides run `__fixtures__/monthlyBillCases.json`; a case they disagree on fails
 * on whichever side is wrong. Server side:
 * `backend/functions/src/__tests__/monthlyBillMirror.test.ts`.
 */

export interface MonthlyBillInput {
  /** NET monthly cents, written from Stripe. `0` is a real answer (comped). */
  mrr?: unknown
}

/** Monthly cents this client pays, or `null` when we genuinely do not know. */
export function resolveMonthlyBillCents(client: MonthlyBillInput | null | undefined): number | null {
  const mrr = client?.mrr
  if (typeof mrr !== 'number' || !Number.isFinite(mrr) || mrr < 0) return null
  // ⚠️ 0 RETURNS 0, NOT NULL — a comped client pays nothing and that is known.
  return Math.round(mrr)
}

/** Whole dollars, for surfaces that render `$N/mo`. `null` stays `null`. */
export function resolveMonthlyBillDollars(client: MonthlyBillInput | null | undefined): number | null {
  const cents = resolveMonthlyBillCents(client)
  return cents === null ? null : cents / 100
}

/**
 * Is a return-on-investment comparison meaningful for this client?
 *
 * A comped client has a KNOWN bill of $0, so the number above is correctly 0 —
 * but "0x return on your $0/mo investment" is not a sentence to show anyone.
 */
export function canQuoteRoi(client: MonthlyBillInput | null | undefined): boolean {
  const cents = resolveMonthlyBillCents(client)
  return cents !== null && cents > 0
}
